/* Product page configuration selects. Loaded by sections/main-product.liquid.
   The section emits candidate siblings ([data-config-json]: this product's smallest real collection
   plus its pelfreez.parent_collection, so the other species of the same product are in the pool):
   title, handle, pelfreez.* attributes, variants. A sibling counts only when it is the SAME base
   product -- its title minus the option grammar (species, sterility, hemolysis, processing words,
   anticoagulant, form, age, donor words, Ig depletion, adsorbed / unadsorbed) equals this product's. Species is read across
   the family; every other axis (Anticoagulant, Sterility, Hemolysis, Processing, Depletion, Form,
   Donor age / sex / type, Blood type) only across the siblings of this product's species. Form is
   physical_state; a tissue (Animal Tissue) is Fresh / Frozen from pelfreez.fresh_frozen, else the title word, and its
   Processing (Stripped / Unstripped, Trimmed / Untrimmed ...) from pelfreez.processing_method, else the title word; a whole
   tissue (pelfreez.fresh_on_request, [data-fresh-on-request]) always offers Fresh, and choosing it with no fresh
   sibling enters fresh mode (note + Contact Us, buy buttons locked) instead of navigating. Each axis
   that differs becomes a select above Size; choosing another value opens the
   sibling that carries it and agrees with the current product on the most other axes, keeping the
   chosen size when that sibling sells the same pack. Pure helpers are exposed on
   window.theme.productConfig so tests can run them under node.
   The attribute glossary (assets/attribute-glossary.json, copied from
   pipeline/reference/content/storefront_attribute_glossary.json) is fetched once per page: it gives
   every configuration select an info button + tooltip (axis sentence + the selected value's
   sentence) and its options a title, and puts an "About this variant" info button on the Size row
   ([data-attribute-notes]: one paragraph per attribute this product carries that has no select of
   its own, glossary sentence + optional blog link). Attributes without a glossary sentence render
   nothing. */
(function () {
  'use strict';

  var AXES = ['species', 'anticoagulant', 'sterility', 'hemolysis', 'processing', 'tissue_processing', 'depletion', 'absorption', 'form',
              'age', 'donor_sex', 'donor_type', 'blood_type', 'grade'];
  // Axes whose blank value is a real choice (the plain product): shown as "None". On every other axis a
  // blank is a missing metafield, not an alternative -- offered only when the current product is blank.
  // grade: the plain product (no grade word) is the premium one -- shown as "Premium" (labels._premium), the marked
  // one is "Standard Grade" (31061 / 31062 baby rabbit complement: <1 vs <50 EU/mL endotoxin)
  var NONE_AXES = { anticoagulant: true, processing: true, depletion: true, grade: true };
  var GRADE_WORDS = /,?\s*\b(Standard|Premium) Grade\b/i;
  var GRADE_CANON = { premium: '', 'premium grade': '', standard: 'Standard Grade', 'standard grade': 'Standard Grade' };
  // The treatments PF and its resale vendors sell as variants of one product. Anything else in the
  // Magento multi-select product_attributes (Lyophilized = Form, Antibody Depleted = Depletion, OTC,
  // Blood Fraction, Low Endotoxin, Affinity Purified ...) is a descriptor, never a select option.
  var PROCESSING = ['Heat Inactivated', 'Delipidized', 'Charcoal Stripped', 'Defibrinated', 'Filtered'];
  // Tissue processing (the CRM's tissue Processing slot; pelfreez.processing_method on a tissue, else the title
  // word): a stripped and an unstripped brain, a trimmed and an untrimmed lung are one product sold two ways.
  var TISSUE_PROCESSING = ['Stripped', 'Unstripped', 'Trimmed', 'Untrimmed', 'Washed', 'Unwashed', 'Perfused', 'Defatted', 'Skinned', 'Atria Removed'];
  var TISSUE_PROCESSING_WORDS = /\b(Unstripped|Stripped|Untrimmed|Trimmed|Unwashed|Washed|Perfused|Defatted|Skinned|Atria Removed)\b/i;
  var IG_ORDER = ['IgG Depleted', 'IgM Depleted', 'IgA Depleted'];
  // sera carry no age metafield: the title word is the CRM's own age vocabulary (crm_attributes.age)
  var AGE_WORDS = /\b(Fetal|Newborn|Neonatal|Young|Mature|Adult)\b/;
  var AGE_CANON = { Neonatal: 'Newborn', Adult: 'Mature' };
  var SEX_CANON = { Either: 'Mixed' };
  var FORM_WORDS = /\b(Fresh|Frozen)\b/;
  // complement is sold adsorbed / unadsorbed (31063 vs 31060): the metafield, else the title word
  var ABSORPTION_WORDS = /\b(Unadsorbed|Unabsorbed|Adsorbed|Absorbed)\b/i;
  var ABSORPTION_CANON = { unabsorbed: 'Unadsorbed', absorbed: 'Adsorbed', unadsorbed: 'Unadsorbed', adsorbed: 'Adsorbed' };
  // The title grammar that spells an option; what is left after removing it is the base product.
  // Grade words, organ qualifiers ("with Optic Nerve"), "3X Concentrated", "Mini Pig", "C1q Depleted"
  // are not options and stay, so those remain separate products.
  var GRAMMAR = [
    /\s*[,(]\s*Neat\)?/gi, /\bOTC\b|\bOff[- ](?:the[- ])?Clot\b/gi,
    /\bIg[GAM](?:\/Ig[GAM])*\s+Depleted\b/gi, /\(\s*(?:[A-Za-z]+\s+)?Ig[GAM]\s*\)/g,
    /\b(?:Non-)?Sterile\b/gi, /\b(?:Non-|Trace-)?Hemoly[sz]ed\b/gi,
    /\bHeat[- ]Inactivated\b/gi, /\bDelipid(?:ized|ated)\b/gi, /\bCharcoal[- ]Stripped\b/gi, /\bDefibrinated\b/gi, /\bFiltered\b/gi,
    /\b(?:Un)?stripped\b/gi, /\b(?:Un)?trimmed\b/gi, /\b(?:Un)?washed\b/gi, /\bPerfused\b/gi, /\bDefatted\b/gi, /\bSkinned\b/gi, /\bAtria Removed\b/gi,
    /\b(?:Un)?A[bd]sorbed\b/gi, /\bLyophilized\b/gi, /\bPooled\b/gi, /\bSingle Donor\b/gi, /\bDonor Herd\b/gi, /\b(?:Male|Female)\b/gi,
    /\bType (?:AB|A|B|O)\b/g, /\b(?:Fetal|Newborn|Neonatal|Young|Mature|Adult)\b/g, /\b(?:Fresh|Frozen)\b/g,
    /,?\s*\b(?:Standard|Premium) Grade\b/gi
  ];

  function asList(v) {
    if (Array.isArray(v)) return v;
    if (typeof v === 'string' && v.charAt(0) === '[') { try { return JSON.parse(v); } catch (e) { return [v]; } }
    return v ? [v] : [];
  }

  function escapeRe(s) { return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); }

  /* The base product a title names: the option grammar, the product's own anticoagulant ("with
     K2 EDTA" / "with ACD", or the comma clause ", K2 EDTA" of the CRM name grammar) and its species
     words removed, whitespace and stray commas collapsed. */
  function family(p) {
    var t = String(p.title || '');
    var ac = String(p.anticoagulant || '');
    if (ac) {
      var forms = [escapeRe(ac)];
      var m = /\(([^)]+)\)/.exec(ac);
      if (m) forms.push(escapeRe(m[1]));
      t = t.replace(new RegExp('(?:\\s+with\\s+|,\\s*)(?:' + forms.join('|') + ')(?![\\w-])', 'i'), '');
    }
    GRAMMAR.forEach(function (rx) { t = t.replace(rx, ''); });
    String(p.species || '').split(/\s+/).forEach(function (w) {
      if (w) t = t.replace(new RegExp('\\b' + escapeRe(w) + '\\b', 'gi'), '');
    });
    return t.replace(/\(\s*\)/g, '').replace(/\s+/g, ' ').replace(/\s*,\s*(?=,|$)/g, '').replace(/^[\s,-]+|[\s,-]+$/g, '');
  }

  /* One value per axis for a sibling. product_attributes is Magento's multi-select
     ("Sterile", "Non-Hemolyzed", "Heat Inactivated"): sterility and hemolysis are single-valued
     groups inside it, the whitelisted treatments are the processing set, the rest is ignored. */
  function attrs(p) {
    var sterility = '', hemolysis = '', processing = [];
    asList(p.product_attributes).forEach(function (v) {
      if (/^(Non-)?Sterile$/i.test(v)) sterility = v;
      else if (/Hemoly[sz]ed/i.test(v)) hemolysis = v;
      else if (PROCESSING.indexOf(v) >= 0) processing.push(v);
    });
    var title = p.title || '';
    var age = p.age || '';
    if (!age) {
      var m = AGE_WORDS.exec(title);
      if (m) age = AGE_CANON[m[1]] || m[1];
    }
    var form = p.physical_state || '';
    var tissueProcessing = '';
    if (form === 'Animal Tissue' || !form) {   // tissue is sold fresh or frozen: the fresh_frozen metafield, else the title word
      var f = FORM_WORDS.exec(title);
      form = p.fresh_frozen || (f ? f[1] : '');
      // the tissue's processing: the processing_method metafield entry the CRM slot spells, else the title word
      asList(p.processing_method).forEach(function (v) {
        if (!tissueProcessing && TISSUE_PROCESSING.indexOf(v) >= 0) tissueProcessing = v;
      });
      if (!tissueProcessing) {
        var tp = TISSUE_PROCESSING_WORDS.exec(title);
        if (tp) tissueProcessing = TISSUE_PROCESSING.filter(function (v) { return v.toLowerCase() === tp[1].toLowerCase(); })[0] || '';
      }
    }
    var igs = asList(p.depletion_process).filter(function (d) { return IG_ORDER.indexOf(d) >= 0; })
      .sort(function (a, b) { return IG_ORDER.indexOf(a) - IG_ORDER.indexOf(b); })
      .map(function (d) { return d.split(' ')[0]; });
    var sex = p.donor_sex || '';
    var absorption = String(p.absorption || '');
    if (!absorption) {
      var ab = ABSORPTION_WORDS.exec(title);
      if (ab) absorption = ab[1];
    }
    absorption = absorption ? (ABSORPTION_CANON[absorption.toLowerCase()] || absorption) : '';
    // grade: the metafield, else the title clause; Premium is the plain product (blank), Standard Grade the marked one
    var grade = String(p.grade || '');
    if (!grade) { var gm = GRADE_WORDS.exec(title); if (gm) grade = gm[1] + ' Grade'; }
    grade = GRADE_CANON[grade.toLowerCase()] !== undefined ? GRADE_CANON[grade.toLowerCase()] : grade;
    return {
      species: p.species || '',
      anticoagulant: p.anticoagulant || '',
      sterility: sterility,
      hemolysis: hemolysis,
      processing: processing.slice().sort().join(', '),
      tissue_processing: tissueProcessing,
      depletion: igs.length ? igs.join('/') + ' Depleted' : '',
      absorption: absorption,
      form: form,
      age: age,
      donor_sex: SEX_CANON[sex] || sex,
      donor_type: p.donor_type || '',
      blood_type: p.blood_type || '',
      grade: grade
    };
  }

  /* The candidates that are the same base product as `current` (current included, duplicates by handle dropped). */
  function siblingsOf(candidates, current) {
    var key = family(current), seen = {};
    return candidates.filter(function (s) {
      if (seen[s.handle] || family(s) !== key) return false;
      seen[s.handle] = true;
      return true;
    });
  }

  /* The siblings an axis is read from: the whole family for Species, otherwise only this species
     (a Goat-only treatment is not offered on the Chicken Serum page). */
  function pool(siblings, current, axis) {
    if (axis === 'species') return siblings;
    return siblings.filter(function (s) { return s.attrs.species === current.attrs.species; });
  }

  /* The axes worth a select, in AXES order, values sorted with the blank one last. A "None" axis
     needs two distinct values, blank counting; any other axis needs two distinct non-blank values
     and lists the blank only when the current product itself is blank there.
     opts.freshOnRequest (pelfreez.fresh_on_request, a whole tissue): the Form axis always offers
     Fresh beside Frozen -- fresh tissue is collected to order, so the choice exists even when no
     fresh sibling is sold online (choosing it then enters fresh mode, see setFreshMode). */
  function axes(siblings, current, opts) {
    var out = [];
    var fresh = !!(opts && opts.freshOnRequest);
    AXES.forEach(function (key) {
      var values = [];
      pool(siblings, current, key).forEach(function (s) { if (values.indexOf(s.attrs[key]) < 0) values.push(s.attrs[key]); });
      if (fresh && key === 'form') {
        ['Fresh', 'Frozen'].forEach(function (v) { if (values.indexOf(v) < 0) values.push(v); });
      }
      var real = values.filter(function (v) { return v; });
      if (NONE_AXES[key] ? values.length < 2 : real.length < 2) return;
      if (!NONE_AXES[key] && !(current && !current.attrs[key])) values = real;
      values.sort(function (a, b) { return !a ? 1 : !b ? -1 : a.localeCompare(b); });
      out.push({ key: key, values: values });
    });
    return out;
  }

  /* Fresh mode on a product page: the fresh-collection note + Contact Us ([data-fresh-request],
     rendered by main-product.liquid) shows, Add to Order / Add to Quote lock (product-form.js keeps
     the lock across size changes via data-fresh-locked), the stock lead time hides. */
  function setFreshMode(root, on) {
    var box = root.querySelector('[data-fresh-request]');
    if (box) box.hidden = !on;
    var reason = box ? (box.querySelector('[data-fresh-note]') || box).textContent.trim() : '';
    var add = root.querySelector('[data-add-to-cart]');
    if (add) {
      if (on) {
        if (!add.hasAttribute('data-fresh-unbuyable')) add.setAttribute('data-fresh-unbuyable', add.disabled ? '1' : '');
        add.setAttribute('data-fresh-locked', '1');
        add.disabled = true;
        add.title = reason;
      } else {
        add.removeAttribute('data-fresh-locked');
        add.removeAttribute('title');
        add.disabled = add.getAttribute('data-fresh-unbuyable') === '1';
      }
    }
    var quote = root.querySelector('.product__quote');
    if (quote) {
      quote.classList.toggle('is-disabled', on);
      if (on) { quote.setAttribute('aria-disabled', 'true'); quote.setAttribute('tabindex', '-1'); quote.title = reason; }
      else { quote.removeAttribute('aria-disabled'); quote.removeAttribute('tabindex'); quote.removeAttribute('title'); }
    }
    var lead = root.querySelector('[data-lead-row]');
    if (lead) lead.hidden = on;
  }

  /* The sibling carrying `value` on `axis` that agrees with `current` on the most other axes
     (Sterile is not offered for every anticoagulant: asking for it lands on the nearest Sterile one). */
  function pick(siblings, current, axis, value) {
    var best = null, bestScore = -1;
    pool(siblings, current, axis).forEach(function (s) {
      if (s.attrs[axis] !== value) return;
      var score = 0;
      AXES.forEach(function (k) { if (k !== axis && s.attrs[k] === current.attrs[k]) score++; });
      if (score > bestScore) { best = s; bestScore = score; }
    });
    return best;
  }

  /* The sibling's product URL, preselecting the variant with the same size title when it has one.
     Built from the handle (not a URL string out of the page) so only a /products/ path can be navigated to. */
  function targetUrl(sibling, sizeTitle) {
    var url = '/products/' + encodeURIComponent(String(sibling.handle || ''));
    if (sizeTitle) {
      var same = (sibling.variants || []).filter(function (v) { return v.title === sizeTitle; })[0];
      if (same) url += '?variant=' + encodeURIComponent(String(same.id));
    }
    return url;
  }

  function optionLabel(labels, key, value) {
    if (key === 'grade' && !value) return labels._premium || 'Premium';
    return value || labels[NONE_AXES[key] ? '_none' : '_unspecified'] || '';
  }

  /* The glossary sentence for one axis value: processing is a sorted "A, B" of treatments, each
     with its own entry. Returns [{value, text, href}] (empty when the glossary has nothing). */
  function valueNotes(glossary, key, value) {
    var axis = glossary && glossary.axes && glossary.axes[key];
    if (!axis || !value) return [];
    var parts = key === 'processing' ? String(value).split(', ') : [value];
    return parts.map(function (v) {
      var e = axis.values && axis.values[v];
      return e ? { value: v, text: e.text || '', href: e.read_more || '' } : null;
    }).filter(Boolean);
  }

  /* The "About this variant" lines for a product: every axis value it carries that has a glossary
     sentence, in AXES order, then the descriptors (product_attributes values / title words that are
     facts but never a select option: Off-the-Clot, Donor Herd). */
  function notes(p, glossary) {
    var a = p.attrs || attrs(p), out = [];
    AXES.forEach(function (key) {
      valueNotes(glossary, key, a[key]).forEach(function (n) { out.push({ axis: key, value: n.value, text: n.text, href: n.href }); });
    });
    var title = String(p.title || ''), pa = asList(p.product_attributes);
    var desc = (glossary && glossary.descriptors) || {};
    Object.keys(desc).forEach(function (word) {
      var words = [word].concat(desc[word].match || []);
      var hit = pa.indexOf(word) >= 0 || words.some(function (w) { return new RegExp('(^|[^\\w])' + escapeRe(w) + '(?![\\w])', 'i').test(title); });
      if (hit) out.push({ axis: '', value: word, text: desc[word].text || '', href: desc[word].read_more || '' });
    });
    return out;
  }

  var glossaryPromise = null;
  function loadGlossary(url) {
    if (!url || typeof window.fetch !== 'function') return Promise.resolve(null);
    if (!glossaryPromise) {
      glossaryPromise = window.fetch(url, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    }
    return glossaryPromise;
  }

  /* One tooltip paragraph: "<strong>Value.</strong> sentence [Read more]" */
  function noteParagraph(n, more) {
    var q = document.createElement('p');
    var b = document.createElement('strong');
    b.textContent = n.value + '.';
    q.appendChild(b);
    q.appendChild(document.createTextNode(' ' + n.text));
    if (more && n.href && n.href.indexOf('/') === 0) {
      q.appendChild(document.createTextNode(' '));
      var a = document.createElement('a');
      a.href = n.href;
      a.textContent = more;
      q.appendChild(a);
    }
    return q;
  }

  /* Info button after a buy-box row's label + the tooltip it describes, placed in the row's field:
     hover / focus shows it, click pins it (so a link inside can be reached), the pointer may cross
     onto the tip, Escape closes. `fill(tip)` writes the content; returns { tip, fill } so a select's
     change can refill. */
  function attachInfo(row, id, ariaLabel, fill) {
    var tip = document.createElement('div');
    tip.className = 'buybox__tip';
    tip.id = id;
    tip.setAttribute('role', 'tooltip');
    tip.hidden = true;
    function refill() { tip.textContent = ''; fill(tip); }
    refill();
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'buybox__info';
    btn.setAttribute('aria-label', ariaLabel);
    btn.setAttribute('aria-expanded', 'false');
    btn.setAttribute('aria-describedby', tip.id);
    btn.textContent = 'i';
    var pinned = false, overTip = false, timer = null;
    function show(on) { tip.hidden = !on; btn.setAttribute('aria-expanded', on ? 'true' : 'false'); }
    function leave() { clearTimeout(timer); timer = setTimeout(function () { if (!pinned && !overTip) show(false); }, 150); }
    btn.addEventListener('mouseenter', function () { clearTimeout(timer); show(true); });
    btn.addEventListener('mouseleave', leave);
    btn.addEventListener('focus', function () { show(true); });
    btn.addEventListener('blur', function () { if (!pinned) show(false); });
    btn.addEventListener('click', function () { pinned = !pinned; show(pinned); });
    btn.addEventListener('keydown', function (e) { if (e.key === 'Escape') { pinned = false; show(false); } });
    tip.addEventListener('mouseenter', function () { overTip = true; clearTimeout(timer); });
    tip.addEventListener('mouseleave', function () { overTip = false; leave(); });
    var lab = row.querySelector('.buybox__label');
    var cell = document.createElement('div');
    cell.className = 'buybox__labelcell';
    row.insertBefore(cell, lab);
    cell.appendChild(lab);
    cell.appendChild(btn);
    (row.querySelector('.buybox__field') || row).appendChild(tip);
    return { tip: tip, fill: refill };
  }

  /* "About this variant" on the Size row: the product's glossary lines for every axis that has NO
     configuration select of its own (those carry their own tooltip) plus the descriptors. */
  function renderNotes(row, product, glossary, selectAxes) {
    var lines = notes(product, glossary).filter(function (n) { return !n.axis || selectAxes.indexOf(n.axis) < 0; });
    if (!lines.length) return;
    var about = row.getAttribute('data-str-about') || 'About this variant';
    var more = row.getAttribute('data-str-read-more') || 'Read more';
    attachInfo(row, 'about-variant-tip', about, function (tip) {
      lines.forEach(function (n) { tip.appendChild(noteParagraph(n, more)); });
    });
  }

  /* Info button after the select's label + a tooltip (axis sentence, then the selected value's) the
     select is described by. Options get the value sentence as their title. */
  function addTooltip(row, sel, key, glossary, labels) {
    var axis = glossary && glossary.axes && glossary.axes[key];
    if (!axis) return;
    // the grade select's blank option is the plain product: its note is the glossary's 'Premium' entry
    var noteValue = function (v) { return key === 'grade' && !v ? 'Premium' : v; };
    Array.prototype.forEach.call(sel.options, function (o) {
      var t = valueNotes(glossary, key, noteValue(o.value)).map(function (n) { return n.text; }).join(' ');
      if (t) o.title = t;
    });
    var info = attachInfo(row, sel.id + '-tip', (labels._info || 'About') + ' ' + (labels[key] || key), function (tip) {
      var p = document.createElement('p');
      p.textContent = axis.axis || '';
      tip.appendChild(p);
      valueNotes(glossary, key, noteValue(sel.value)).forEach(function (n) { tip.appendChild(noteParagraph(n, '')); });
    });
    sel.addEventListener('change', info.fill);
    sel.setAttribute('aria-describedby', info.tip.id);
  }

  function initConfig(root, labels) {
    var rows = root.querySelector('[data-config-rows]');
    var dataEl = root.querySelector('[data-config-json]');
    if (!rows || !dataEl || rows.children.length) return null;
    var candidates;
    try { candidates = JSON.parse(dataEl.textContent); } catch (e) { return null; }
    var current = candidates.filter(function (s) { return s.current; })[0];
    if (!current) return null;
    var siblings = siblingsOf(candidates, current);
    siblings.forEach(function (s) { s.attrs = attrs(s); });
    current.attrs = current.attrs || attrs(current);
    var freshOnRequest = rows.getAttribute('data-fresh-on-request') === '1';
    var list = axes(siblings, current, { freshOnRequest: freshOnRequest });
    if (!list.length) return null;

    var sectionId = rows.getAttribute('data-section-id') || '';
    var sizeSelect = root.querySelector('[data-variant-select]');
    function currentSizeTitle() {
      var id = sizeSelect && sizeSelect.value;
      var v = id ? (current.variants || []).filter(function (x) { return String(x.id) === String(id); })[0] : null;
      return v ? v.title : '';
    }

    var built = [];
    list.forEach(function (axis) {
      var row = document.createElement('div');
      row.className = 'buybox__row';
      var lab = document.createElement('label');
      lab.className = 'buybox__label';
      lab.htmlFor = 'cfg-' + axis.key + '-' + sectionId;
      lab.textContent = (labels[axis.key] || axis.key) + ':';
      var field = document.createElement('div');
      field.className = 'buybox__field';
      var sel = document.createElement('select');
      sel.className = 'buybox__select';
      sel.id = lab.htmlFor;
      sel.setAttribute('data-config-select', axis.key);
      axis.values.forEach(function (v) {
        var o = document.createElement('option');
        o.value = v;
        o.textContent = optionLabel(labels, axis.key, v);
        o.selected = v === current.attrs[axis.key];
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () {
        var target = pick(siblings, current, axis.key, sel.value);
        if (freshOnRequest && axis.key === 'form' && current.attrs.form !== 'Fresh') {
          // a frozen tissue: Fresh with no fresh sibling = fresh mode on this page; back to Frozen releases it
          if (sel.value === 'Fresh' && !target) { setFreshMode(root, true); return; }
          if (sel.value !== 'Fresh') setFreshMode(root, false);
        }
        if (!target || target === current) return;
        window.location.assign(targetUrl(target, currentSizeTitle()));
      });
      field.appendChild(sel);
      row.appendChild(lab);
      row.appendChild(field);
      rows.appendChild(row);
      built.push({ row: row, sel: sel, key: axis.key });
    });
    var box = rows.closest('.product__buybox');
    if (box) box.classList.add('buybox--config');
    return { rows: rows, built: built };
  }

  function init(root) {
    var labelsEl = root.querySelector('[data-config-labels]');
    var labels = {};
    try { labels = labelsEl ? JSON.parse(labelsEl.textContent) : {}; } catch (e) { labels = {}; }
    var config = initConfig(root, labels);
    var notesRow = root.querySelector('[data-attribute-notes]');
    if (notesRow && notesRow.getAttribute('data-notes-done')) notesRow = null;
    var product = null, itemEl = notesRow && notesRow.querySelector('[data-attribute-item]');
    if (itemEl) { try { product = JSON.parse(itemEl.textContent); } catch (e) { product = null; } }
    var url = (notesRow && notesRow.getAttribute('data-glossary-url')) || (config && config.rows.getAttribute('data-glossary-url'));
    if (!url || (!product && !config)) return;
    loadGlossary(url).then(function (glossary) {
      if (!glossary) return;
      var selectAxes = config ? config.built.map(function (b) { return b.key; }) : [];
      if (config) config.built.forEach(function (b) { addTooltip(b.row, b.sel, b.key, glossary, labels); });
      if (notesRow && product) { notesRow.setAttribute('data-notes-done', '1'); renderNotes(notesRow, product, glossary, selectAxes); }
    });
  }

  window.theme = window.theme || {};
  window.theme.productConfig = { family: family, attrs: attrs, siblingsOf: siblingsOf, axes: axes, pick: pick, targetUrl: targetUrl,
                                 valueNotes: valueNotes, notes: notes, setFreshMode: setFreshMode };
  if (window.theme.register) window.theme.register(init);
})();
